// Hand-rolled fetch hook for the /ai_manifesto explorer — same shape as the
// other src/lib hooks (no SWR: this is a user-triggered POST, not a cached
// GET). Holds a small state machine: idle → loading → success | error.

import { useCallback, useState } from 'react';

export type ManifestoLanguage = 'en' | 'mt';

export interface ManifestoUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
}

export interface ManifestoResponse {
  answer: string;
  language: ManifestoLanguage;
  /** 'comparative' = the answer parses as the Compare JSON shape and
   *  should render as the side-by-side card. 'prose' = render as a
   *  markdown answer with inline citations. Replaces the old 'mode'
   *  field that the reader used to pick explicitly. */
  shape: 'comparative' | 'prose';
  usage: ManifestoUsage;
  latencyMs: number;
  estimatedCostCents: number;
}

export interface ManifestoError {
  code: string;
  message: string;
  retryAfterSeconds?: number;
}

// Shape of the JSON returned by the model in Compare mode (parsed from
// ManifestoResponse.answer).
export interface CompareSide {
  summary: string;
  specifics: string[];
  citations: string[];
}
export interface CompareData {
  topic: string;
  pl: CompareSide;
  pn: CompareSide;
  convergence: string;
  divergence: string;
  tradeoffs: string;
  /** HONEST APPRAISAL paragraph — structural critique of the
   *  proposals, applied symmetrically across both parties. Renders as
   *  its own panel so it can't get lost inside "Trade-offs". Old
   *  responses missing the field render with no appraisal panel; new
   *  responses always include it. */
  appraisal: string;
}

export type ManifestoQueryState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; response: ManifestoResponse }
  | { status: 'error'; error: ManifestoError };

export interface ManifestoChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface SubmitOptions {
  /** Full conversation history including the new user turn. Last
   *  element must be a user message. */
  messages: ManifestoChatMessage[];
  language: ManifestoLanguage;
}

export type ManifestoSubmitResult =
  | { status: 'success'; response: ManifestoResponse }
  | { status: 'error'; error: ManifestoError };

export function useManifestoQuery(): {
  state: ManifestoQueryState;
  submit: (opts: SubmitOptions) => Promise<ManifestoSubmitResult>;
  reset: () => void;
} {
  const [state, setState] = useState<ManifestoQueryState>({ status: 'idle' });

  const submit = useCallback(
    async (opts: SubmitOptions): Promise<ManifestoSubmitResult> => {
      setState({ status: 'loading' });
      try {
        const res = await fetch('/api/manifesto/query', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(opts),
        });
        const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;

        if (!res.ok) {
          const error: ManifestoError = {
            code: typeof data.error === 'string' ? data.error : 'error',
            message:
              typeof data.message === 'string'
                ? data.message
                : 'Something went wrong. Please try again.',
            retryAfterSeconds:
              typeof data.retryAfterSeconds === 'number'
                ? data.retryAfterSeconds
                : undefined,
          };
          setState({ status: 'error', error });
          return { status: 'error', error };
        }
        const response = data as unknown as ManifestoResponse;
        setState({ status: 'success', response });
        return { status: 'success', response };
      } catch {
        const error: ManifestoError = {
          code: 'network',
          message: 'Network error. Please try again.',
        };
        setState({ status: 'error', error });
        return { status: 'error', error };
      }
    },
    [],
  );

  const reset = useCallback(() => setState({ status: 'idle' }), []);

  return { state, submit, reset };
}

/** Parse a Compare-mode answer string into CompareData, or null on failure. */
export function parseCompareData(answer: string): CompareData | null {
  try {
    const parsed = JSON.parse(answer) as Partial<CompareData>;
    if (!parsed || typeof parsed !== 'object' || !parsed.pl || !parsed.pn) {
      return null;
    }
    const side = (s: Partial<CompareSide> | undefined): CompareSide => ({
      summary: typeof s?.summary === 'string' ? s.summary : '',
      specifics: Array.isArray(s?.specifics) ? s!.specifics.map(String) : [],
      citations: Array.isArray(s?.citations) ? s!.citations.map(String) : [],
    });
    return {
      topic: typeof parsed.topic === 'string' ? parsed.topic : '',
      pl: side(parsed.pl),
      pn: side(parsed.pn),
      convergence: typeof parsed.convergence === 'string' ? parsed.convergence : '',
      divergence: typeof parsed.divergence === 'string' ? parsed.divergence : '',
      tradeoffs: typeof parsed.tradeoffs === 'string' ? parsed.tradeoffs : '',
      appraisal: typeof parsed.appraisal === 'string' ? parsed.appraisal : '',
    };
  } catch {
    return null;
  }
}
